import { useEffect, useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Close, HomeOutlined } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { MutatorOptions } from 'swr';
import { CountryFlag } from '../../CountryFlag';
import { useFetchService } from '../../../utils/fetchService';
import { useLocalStorage } from '../../../utils/useLocalStorage';
import { sendHttpRequest } from '../../../utils/network.service';
import { OrganizationSettingCard } from './OrganizationSettingCard';
import { OrganizationSettingsSnackbarDismissButton } from './OrganizationSettingsDialog';
import { CountryPickerModal } from './CountryPickerModal';
import { CountryCatalogEntry, CountryOption, createCountryOptions } from './countrySettings';

interface HomeCountryConfigurationCardProps {
  isAdmin: boolean;
  currentHomeCountry?: string;
  lastUpdated?: string;
  mutate: (data?: unknown, opts?: boolean | MutatorOptions<unknown>) => Promise<unknown>;
}

interface HighRiskCountriesResponse {
  highRiskCountries: string[];
  total: number;
}

const DEFAULT_HOME_COUNTRY: CountryOption = {
  label: 'United States of America',
  value: 'US',
};

export const HomeCountryConfigurationCard = ({ isAdmin, currentHomeCountry, lastUpdated, mutate }: HomeCountryConfigurationCardProps) => {
  const organizationHomeCountryCode = currentHomeCountry?.toUpperCase() || DEFAULT_HOME_COUNTRY.value;
  const [savedHomeCountryCode, setSavedHomeCountryCode] = useState(organizationHomeCountryCode);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [pendingHomeCountryCode, setPendingHomeCountryCode] = useState(organizationHomeCountryCode);
  const [isSaving, setIsSaving] = useState(false);
  const [localLastUpdated, setLocalLastUpdated] = useState<string | Date | undefined>(lastUpdated);
  const [accessToken = ''] = useLocalStorage('accessToken', '');
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { data: countriesData, error: countriesError } = useFetchService<CountryCatalogEntry[]>('/list/countryCodes', accessToken);
  const { data: highRiskCountriesData, error: highRiskCountriesError } = useFetchService<HighRiskCountriesResponse>(
    '/list/high-risk-countries',
    accessToken,
  );

  const countryOptions = useMemo(() => {
    if (!countriesData?.length) return [DEFAULT_HOME_COUNTRY];

    return createCountryOptions(countriesData);
  }, [countriesData]);

  const highRiskCountryCodes = useMemo(
    () => new Set((highRiskCountriesData?.highRiskCountries || []).map(countryCode => countryCode.toUpperCase())),
    [highRiskCountriesData],
  );
  const pickerOptions = useMemo(
    () =>
      countryOptions.map(option => ({
        ...option,
        disabledReason: highRiskCountryCodes.has(option.value) ? 'On risky nations list' : undefined,
      })),
    [countryOptions, highRiskCountryCodes],
  );

  const savedHomeCountry = countryOptions.find(country => country.value === savedHomeCountryCode) || {
    label: savedHomeCountryCode,
    value: savedHomeCountryCode,
  };
  const pendingHomeCountry = countryOptions.find(country => country.value === pendingHomeCountryCode) || null;
  const isPendingHomeCountryHighRisk = highRiskCountryCodes.has(pendingHomeCountryCode);
  const isSaveDisabled =
    isSaving ||
    !pendingHomeCountry ||
    !highRiskCountriesData ||
    Boolean(highRiskCountriesError) ||
    isPendingHomeCountryHighRisk ||
    pendingHomeCountryCode === savedHomeCountryCode;

  useEffect(() => {
    setSavedHomeCountryCode(organizationHomeCountryCode);
  }, [organizationHomeCountryCode]);

  useEffect(() => {
    if (!isEditorOpen) setPendingHomeCountryCode(savedHomeCountryCode);
  }, [isEditorOpen, savedHomeCountryCode]);

  useEffect(() => {
    if (lastUpdated) setLocalLastUpdated(lastUpdated);
  }, [lastUpdated]);

  const closeEditor = () => {
    if (isSaving) return;

    setIsEditorOpen(false);
    setPendingHomeCountryCode(savedHomeCountryCode);
  };

  const openEditor = () => {
    setPendingHomeCountryCode(savedHomeCountryCode);
    setIsEditorOpen(true);
  };

  const updateHomeCountry = async () => {
    if (isSaveDisabled || !pendingHomeCountry) return;

    setIsSaving(true);
    const result = await sendHttpRequest({
      path: `${process.env.ORGANIZATION_SERVICE_URL}/organizations/`,
      method: 'PUT',
      body: { homeCountryCode: pendingHomeCountry.value },
      successMessage: `Home country set to ${pendingHomeCountry.label}.`,
      errorMessage: 'Error updating home country.',
      enqueueSnackbar,
      successOptions: {
        action: snackbarId => (
          <OrganizationSettingsSnackbarDismissButton
            aria-label="Dismiss notification"
            size="small"
            onClick={() => closeSnackbar(snackbarId)}
          >
            <Close fontSize="small" />
          </OrganizationSettingsSnackbarDismissButton>
        ),
      },
    });
    setIsSaving(false);

    if (result.error) return;

    setSavedHomeCountryCode(pendingHomeCountry.value);
    setLocalLastUpdated(new Date());
    setIsEditorOpen(false);
    await mutate();
  };

  return (
    <>
      <OrganizationSettingCard
        title="Home Country"
        description="Used to determine which Server Name Indicator (SNI) geolocations discovered during network analysis of an app are considered foreign. A country currently saved as a Risky Nation must first be removed from that list before it can be configured as your Home Country."
        icon={<HomeOutlined />}
        valueLabel="Home country"
        value={
          <CountryValue>
            <CountryFlag countryCode={savedHomeCountry.value} />
            <span>{savedHomeCountry.label}</span>
          </CountryValue>
        }
        lastUpdated={localLastUpdated}
        onEdit={isAdmin && !countriesError ? openEditor : undefined}
      />

      <CountryPickerModal
        id="select-home-country"
        title="Select home country"
        open={isEditorOpen}
        onClose={closeEditor}
        options={pickerOptions}
        selectedValue={pendingHomeCountryCode}
        onSelect={setPendingHomeCountryCode}
        onSave={updateHomeCountry}
        isSaving={isSaving}
        saveDisabled={isSaveDisabled}
        errorMessage={highRiskCountriesError ? 'Risky Nations could not be loaded. Close the editor and try again.' : undefined}
        contentHeight="614px"
        listTestId="home-country-list"
      />
    </>
  );
};

const CountryValue = styled('span')`
  display: inline-flex;
  align-items: center;
  gap: 9px;
`;
